import React, { FC, useEffect } from 'react';
import cx from 'classnames';
import { Asset, InfoFilledIcon } from '@clearcover/atlas';

import { SelectOption } from 'QuoteApp/services/endpoints/GetConfig';
import { PaymentPlanType } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { env } from 'Shared/constants/env';

import { PriceContext, PriceState } from '../domain';
import { WeekdaySelector } from './WeekdaySelector';
import { SemiMonthlySelector } from './SemiMonthlySelector';
import { DayOfMonthSelector } from './DayOfMonthSelector';
import { BiweeklyStartSelector } from './BiweeklyStartSelector';
import { usePriceCustomizer } from '../PriceCustomizerContext';
import { PaymentScheduleModal } from '../../PaymentScheduleModal';
import styles from './styles.module.css';

const DAYS_NOT_IN_ALL_MONTHS = [29, 30, 31];

type OnSelectorChange = (change: { [key: string]: unknown }) => void;

type SelectorContainerType = {
  isLoading?: boolean;
  initialState?: {
    status: PriceState;
    context: Partial<PriceContext>;
  };
  selectorsData: {
    weekday: Array<SelectOption<string>>;
    biweeklyDate: Array<SelectOption<string>>;
    semiMonthly: Array<SelectOption<string>>;
    monthly: Array<SelectOption<string>>;
  };
  onSelectorChange: OnSelectorChange;
};

export const SelectorContainer: FC<SelectorContainerType> = ({
  isLoading,
  initialState,
  selectorsData,
  onSelectorChange,
}) => {
  const {
    status,
    frequency,
    dayOfMonth,
    jumpToState,
    selectedPaymentPlan,
    selectDayOfWeek,
    selectBiweeklyStart,
    selectSemiMonthlyDates,
    selectDayOfMonth,
  } = usePriceCustomizer();

  useEffect(() => {
    if (!initialState) return;

    jumpToState(initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialState?.status,
    initialState?.context.biweeklyDate,
    initialState?.context.dayOfMonth,
    initialState?.context.dayOfWeek,
    initialState?.context.downpayment,
    initialState?.context.frequency,
    initialState?.context.semiMonthlyDates,
  ]);

  const renderInfoBanner = () => {
    return (
      <div className={styles.infoBanner} data-testid="payment-info-banner">
        <Asset
          icon={InfoFilledIcon}
          description="Payment Info"
          height={16}
          width={16}
        />
        <p>
          Don't worry if you pick a date like the 31st - we'll process your
          payment on the last available day of the month.
        </p>
      </div>
    );
  };

  const renderPaymentSchedule = (currentPaymentPlan: PaymentPlanType) => {
    return (
      <div
        className={cx(styles.paymentScheduleContainer, {
          [styles.disabled]: isLoading,
        })}
      >
        <PaymentScheduleModal
          buttonText="See Schedule"
          currentPlan={currentPaymentPlan}
        />
      </div>
    );
  };

  if (!frequency || status === 'idle') {
    return null;
  }

  if (frequency === 'weekly') {
    const dayOfWeekStatuses: PriceState[] = ['selectingDayOfWeek', 'ready'];

    return (
      <div className={styles.selectorContainer}>
        {dayOfWeekStatuses.includes(status) && (
          <div className={cx(styles.selector, styles.z1)}>
            <WeekdaySelector
              name="dayOfWeek"
              isLoading={isLoading}
              options={selectorsData.weekday}
              onChange={(dayOfWeek) => {
                onSelectorChange({ dayOfWeek });
                selectDayOfWeek(dayOfWeek);
              }}
            />
          </div>
        )}

        {selectedPaymentPlan && renderPaymentSchedule(selectedPaymentPlan)}
      </div>
    );
  }

  if (frequency === 'biWeekly') {
    const dayOfWeekStatuses: PriceState[] = [
      'selectingDayOfWeek',
      'selectingBiweeklyDate',
      'ready',
    ];
    const biweeklyStatuses: PriceState[] = ['selectingBiweeklyDate', 'ready'];

    return (
      <div className={styles.selectorContainer}>
        {dayOfWeekStatuses.includes(status) && (
          <div className={cx(styles.selector, styles.z1)}>
            <WeekdaySelector
              name="dayOfWeek"
              isLoading={isLoading}
              options={selectorsData.weekday}
              onChange={(dayOfWeek) => {
                onSelectorChange({ dayOfWeek });
                selectDayOfWeek(dayOfWeek);
              }}
            />
          </div>
        )}

        {biweeklyStatuses.includes(status) &&
          env.paymentFlexibilityEveryTwoWeeks && (
            <div className={cx(styles.selector)}>
              <BiweeklyStartSelector
                options={selectorsData.biweeklyDate}
                onChange={(biweeklyDate) => {
                  onSelectorChange({ biweeklyDate });
                  selectBiweeklyStart(biweeklyDate);
                }}
              />
            </div>
          )}

        {selectedPaymentPlan && renderPaymentSchedule(selectedPaymentPlan)}
      </div>
    );
  }

  if (frequency === 'semiMonthly') {
    const semiMonthlyStatuses: PriceState[] = [
      'selectingSemiMonthlyDates',
      'ready',
    ];

    return (
      <div className={styles.selectorContainer}>
        {semiMonthlyStatuses.includes(status) && (
          <div className={cx(styles.selector, styles.z1)}>
            <SemiMonthlySelector
              isLoading={isLoading}
              options={selectorsData.semiMonthly}
              onChange={(semiMonthlyDates) => {
                onSelectorChange({ semiMonthlyDates });
                selectSemiMonthlyDates(semiMonthlyDates);
              }}
            />
          </div>
        )}

        {selectedPaymentPlan && renderPaymentSchedule(selectedPaymentPlan)}
      </div>
    );
  }

  if (frequency === 'monthly') {
    const monthlyStatuses: PriceState[] = ['selectingDayOfMonth', 'ready'];
    const isIrregularMonthDay =
      dayOfMonth && DAYS_NOT_IN_ALL_MONTHS.includes(parseInt(dayOfMonth, 10));

    return (
      <div className={styles.selectorContainer}>
        {monthlyStatuses.includes(status) && (
          <div className={cx(styles.selector, styles.z1)}>
            <DayOfMonthSelector
              isLoading={isLoading}
              options={selectorsData.monthly}
              onChange={(dayOfMonth) => {
                onSelectorChange({ monthly: dayOfMonth });
                selectDayOfMonth(dayOfMonth);
              }}
            />
          </div>
        )}

        {selectedPaymentPlan && renderPaymentSchedule(selectedPaymentPlan)}

        {isIrregularMonthDay && (
          <div className={cx(styles.selector)}>{renderInfoBanner()}</div>
        )}
      </div>
    );
  }

  if (frequency === 'fullPay') {
    return null;
  }

  return null;
};
